import { useMemo } from "react";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import {
    DraftAccount,
    DraftGroup,
    normalizeGroupName,
} from "./draftOperations";
import { GetRowCapabilities } from "./editorCapabilities";

// Injected code validation. The host owns what makes a code a duplicate, so it
// supplies the rule; the editor body only reads the resulting id-set.
export interface EditorCodeRules {
    findDuplicateCodeIds: (accounts: DraftAccount[]) => Set<string>;
}

export interface EditorDraftValidation {
    invalidNameRowIds: Set<string>;
    invalidGroupNameIds: Set<string>;
    duplicateGroupNameIds: Set<string>;
    duplicateNameIds: Set<string>;
    duplicateCodeIds: Set<string>;
}

// Custom groups with a blank/whitespace name. Mirrors the backend's name.min(1)
// so the editor flags it inline (yellow "Add name") and gates Save before the
// server would reject the bulk save. Pure + exported for unit testing.
export const findInvalidGroupNameIds = (groups: DraftGroup[]): Set<string> => {
    const invalidIds = new Set<string>();
    for (const group of groups) {
        if (group.name.trim().length === 0) {
            invalidIds.add(group.id);
        }
    }
    return invalidIds;
};

interface ScopedName {
    id: string;
    entityId: number;
    type: AccountType;
    parentGroupId: string | null;
    name: string;
}

// Nonblank names collide case-insensitively within exact entity, type, and
// parent scope.
export const toScopedNameKey = ({
    entityId,
    type,
    parentGroupId,
    name,
}: Omit<ScopedName, "id">): string | null => {
    const normalized = normalizeGroupName(name);
    if (normalized.length === 0) {
        return null;
    }
    return `${entityId}::${type}::${parentGroupId ?? "root"}::${normalized}`;
};

const findDuplicateScopedNameIds = (items: ScopedName[]): Set<string> => {
    const idsByScopeName = new Map<string, string[]>();
    for (const item of items) {
        const scopeKey = toScopedNameKey(item);
        if (scopeKey === null) {
            continue;
        }
        const existing = idsByScopeName.get(scopeKey);
        if (existing) {
            existing.push(item.id);
        } else {
            idsByScopeName.set(scopeKey, [item.id]);
        }
    }
    const duplicates = new Set<string>();
    for (const ids of idsByScopeName.values()) {
        if (ids.length > 1) {
            ids.forEach((id) => duplicates.add(id));
        }
    }
    return duplicates;
};

export const findDuplicateGroupNameIds = (groups: DraftGroup[]): Set<string> =>
    findDuplicateScopedNameIds(groups);

export const findDuplicateAccountNameIds = (
    accounts: DraftAccount[],
): Set<string> => findDuplicateScopedNameIds(accounts);

interface UseEditorDraftValidationParams {
    accounts: DraftAccount[];
    // Custom groups in the draft; validated for blank / duplicate names in
    // scope, mirroring account name validation.
    groups: DraftGroup[];
    codeRules: EditorCodeRules;
    getRowCapabilities: GetRowCapabilities;
}

// The draft-only validation the host needs to build the save-blocked reason:
// blank names, group names, and the injected code rules. Reads only accounts +
// groups (no visible rows / selection), so it lives in the host, not the body.
export const useEditorDraftValidation = ({
    accounts,
    groups,
    codeRules,
    getRowCapabilities,
}: UseEditorDraftValidationParams): EditorDraftValidation => {
    const invalidNameRowIds = useMemo(() => {
        const invalidIds = new Set<string>();
        for (const account of accounts) {
            if (getRowCapabilities(account).placeholder != null) {
                continue;
            }
            if (account.name.trim().length === 0) {
                invalidIds.add(account.id);
            }
        }
        return invalidIds;
    }, [accounts, getRowCapabilities]);

    const invalidGroupNameIds = useMemo(
        () => findInvalidGroupNameIds(groups),
        [groups],
    );

    const duplicateGroupNameIds = useMemo(
        () => findDuplicateGroupNameIds(groups),
        [groups],
    );

    const duplicateNameIds = useMemo(
        () => findDuplicateAccountNameIds(accounts),
        [accounts],
    );

    const duplicateCodeIds = useMemo(
        () => codeRules.findDuplicateCodeIds(accounts),
        [accounts, codeRules],
    );

    return {
        invalidNameRowIds,
        invalidGroupNameIds,
        duplicateGroupNameIds,
        duplicateNameIds,
        duplicateCodeIds,
    };
};
